import { useState, useCallback } from 'react';
import { message } from 'antd';
import moment from 'moment-timezone';

import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { useTimezone } from '../../../../core/hooks/useTimezone';

/**
 * useAutoAdjustDates
 *
 * Hook that provides a one-click auto-adjust for past campaign dates.
 *
 * Two modes:
 *  - "shorten"          : shift only START dates to today; end dates stay fixed.
 *                         Campaign runs for fewer days than originally planned.
 *                         If a shifted start would meet or exceed its end,
 *                         the end date is also shifted (safety guard).
 *  - "preserve-duration": shift BOTH start and end dates so the earliest start
 *                         lands on today. Original durations are preserved.
 *                         Flight schedules are shifted as well.
 *
 * @param {Object} options
 * @param {Object} options.currentCampaignOrder
 * @param {number} options.campaignIndex
 * @param {Function} options.updateCampaignStatus — Apollo mutation function
 * @param {Function} options.updateLineItemDspSettings — Apollo mutation function
 * @param {Function} [options.refetchCampaignOrder]
 * @param {Function} [options.onComplete] — called after successful adjustment
 * @returns {{ handleAutoAdjustDates: Function, isAutoAdjusting: boolean }}
 */
const useAutoAdjustDates = ({
  currentCampaignOrder,
  campaignIndex,
  updateCampaignStatus,
  updateLineItemDspSettings,
  refetchCampaignOrder,
  onComplete,
}) => {
  const { timezone } = useTimezone();
  const { updateCampaignOrder } = useCampaignRequestorContext();
  const [isAutoAdjusting, setIsAutoAdjusting] = useState(false);

  const handleAutoAdjustDates = useCallback(
    async (mode = 'preserve-duration') => {
      if (!currentCampaignOrder) return;

      setIsAutoAdjusting(true);

      try {
        const today = moment().startOf('day');
        const campaign = currentCampaignOrder.campaigns[campaignIndex];

        // ------------------------------------------------------------------
        // 1. Collect ALL start dates to find the earliest past one
        // ------------------------------------------------------------------
        const allStartDates = [];

        if (currentCampaignOrder.startDate) {
          allStartDates.push(moment(currentCampaignOrder.startDate).startOf('day'));
        }

        if (campaign?.startDate) {
          allStartDates.push(moment(campaign.startDate).startOf('day'));
        }

        // Campaign-level flight schedules
        const campaignFlights = campaign?.flightSchedules || [];
        campaignFlights.forEach((f) => {
          if (f.startDate) {
            allStartDates.push(moment(f.startDate).startOf('day'));
          }
        });

        // Line items + their flight schedules
        if (campaign?.lineItems) {
          campaign.lineItems.forEach((li) => {
            if (li.startDate) {
              allStartDates.push(moment(li.startDate).startOf('day'));
            }
            const liFlights = li.flightSchedules || li.flightSchedule || [];
            liFlights.forEach((f) => {
              if (f.startDate) {
                allStartDates.push(moment(f.startDate).startOf('day'));
              }
            });
          });
        }

        // Find earliest start date
        let earliestStart = null;
        allStartDates.forEach((d) => {
          if (!earliestStart || d.isBefore(earliestStart)) {
            earliestStart = d;
          }
        });

        if (!earliestStart || !earliestStart.isBefore(today)) {
          message.info('No past dates to adjust');
          if (onComplete) onComplete();
          return;
        }

        const daysToShift = today.diff(earliestStart, 'days');

        // ------------------------------------------------------------------
        // 2. Helper functions for shifting dates
        // ------------------------------------------------------------------
        const shiftDate = (dateStr) => {
          if (!dateStr) return null;
          return moment(dateStr)
            .add(daysToShift, 'days')
            .tz(timezone)
            .startOf('day')
            .utc()
            .toISOString();
        };

        /**
         * For "shorten" mode we only move the start date forward.
         * If the shifted start would meet or exceed its companion end,
         * we also shift the end (safety guard so we never create an
         * invalid date range).
         */
        const shiftStartOnly = (startStr, endStr = null) => {
          if (!startStr) return null;
          const shiftedStart = moment(startStr)
            .add(daysToShift, 'days')
            .tz(timezone)
            .startOf('day');

          if (endStr) {
            const endMoment = moment(endStr).tz(timezone).startOf('day');
            if (!shiftedStart.isBefore(endMoment)) {
              // Start would meet or exceed end — preserve duration for this pair
              return shiftDate(startStr);
            }
          }
          return shiftedStart.utc().toISOString();
        };

        const adjustStart =
          mode === 'shorten' ? shiftStartOnly : shiftDate;
        const adjustEnd = mode === 'shorten' ? (d) => d : shiftDate;

        // ------------------------------------------------------------------
        // 3. Update Campaign Order (start / end only — no flights)
        // ------------------------------------------------------------------
        await updateCampaignOrder({
          variables: {
            where: { id: currentCampaignOrder.id },
            data: {
              startDate: adjustStart(currentCampaignOrder.startDate),
              endDate: adjustEnd(currentCampaignOrder.endDate),
            },
          },
        });

        // ------------------------------------------------------------------
        // 4. Update Campaign + campaign-level flight schedules
        // ------------------------------------------------------------------
        const campaignFlightPayload = {};
        if (campaignFlights.length > 0) {
          campaignFlightPayload.update = campaignFlights.map((f) => ({
            where: { id: f.id },
            data: {
              startDate: adjustStart(f.startDate, f.endDate),
              endDate: adjustEnd(f.endDate),
              budget: Number(f.budget) || 0,
              cpm: Number(f.cpm) || 7,
            },
          }));
        }

        await updateCampaignStatus({
          variables: {
            where: { id: campaign.id },
            data: {
              startDate: adjustStart(campaign.startDate, campaign.endDate),
              endDate: adjustEnd(campaign.endDate),
              ...(Object.keys(campaignFlightPayload).length > 0
                ? { flightSchedules: campaignFlightPayload }
                : {}),
            },
          },
        });

        // ------------------------------------------------------------------
        // 5. Update Line Items + line-item-level flight schedules
        // ------------------------------------------------------------------
        const lineItemPromises = [];
        if (campaign.lineItems) {
          campaign.lineItems.forEach((li) => {
            if (!li.markedForDelivery || !li.startDate) {
              return;
            }

            const liFlights = li.flightSchedules || li.flightSchedule || [];
            const flightPayload = {};

            if (liFlights.length > 0) {
              flightPayload.update = liFlights.map((f) => ({
                where: { id: f.id },
                data: {
                  startDate: adjustStart(f.startDate, f.endDate),
                  endDate: adjustEnd(f.endDate),
                  budget: Number(f.budget) || 0,
                  cpm: Number(f.cpm) || 7,
                },
              }));
            }

            lineItemPromises.push(
              updateLineItemDspSettings({
                variables: {
                  lineItemId: li.id,
                  data: {
                    startDate: adjustStart(li.startDate, li.endDate),
                    endDate: adjustEnd(li.endDate),
                    ...(Object.keys(flightPayload).length > 0
                      ? { flightSchedules: flightPayload }
                      : {}),
                  },
                },
              })
            );
          });
        }

        await Promise.all(lineItemPromises);

        const modeLabel =
          mode === 'shorten'
            ? 'Start dates shifted to today (original end dates kept)'
            : `Dates auto-adjusted forward by ${daysToShift} day(s) (duration preserved)`;
        message.success(modeLabel);

        if (onComplete) onComplete();

        // Refetch to update UI
        // Small delay to ensure mutation has propagated through database
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (refetchCampaignOrder) {
          await refetchCampaignOrder();
        }
      } catch (error) {
        console.error('Auto-adjust dates error:', error);
        message.error(
          'Failed to auto-adjust dates: ' + (error.message || 'Unknown error')
        );
      } finally {
        setIsAutoAdjusting(false);
      }
    },
    [
      currentCampaignOrder,
      campaignIndex,
      timezone,
      updateCampaignOrder,
      updateCampaignStatus,
      updateLineItemDspSettings,
      refetchCampaignOrder,
      onComplete,
    ]
  );

  return { handleAutoAdjustDates, isAutoAdjusting };
};

export default useAutoAdjustDates;
